import {useState, useEffect, useRef, useCallback} from 'react';
import {view, tokens} from '@sail/ui';
import {createKeyframes} from '@sail/engine';

const shimmer = createKeyframes({
  0: {backgroundPosition: '200% center'},
  100: {backgroundPosition: '-200% center'},
});

export const useTypewriterWithBackspace = (
  targetText: string,
  options: {
    typeSpeed?: number;
    backspaceSpeed?: number;
    startDelay?: number;
  } = {},
) => {
  const {typeSpeed = 60, backspaceSpeed = 30, startDelay = 150} = options;

  const [displayText, setDisplayText] = useState('');
  const [isBackspacing, setIsBackspacing] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const currentText = useRef('');
  const currentTargetRef = useRef('');
  const queuedTargetRef = useRef<string | null>(null);
  const isTypingRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearExistingTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const animateText = useCallback(
    (newTargetText: string) => {
      const currentTextValue = currentText.current;
      currentTargetRef.current = newTargetText;

      const typePhase = () => {
        if (currentText.current.length < newTargetText.length) {
          setIsTyping(true);
          isTypingRef.current = true;
          setIsBackspacing(false);
          currentText.current = newTargetText.slice(
            0,
            currentText.current.length + 1,
          );
          setDisplayText(currentText.current);
          timeoutRef.current = setTimeout(typePhase, typeSpeed);
        } else {
          setIsTyping(false);
          isTypingRef.current = false;
          if (
            queuedTargetRef.current &&
            queuedTargetRef.current !== currentText.current
          ) {
            const nextTarget = queuedTargetRef.current;
            queuedTargetRef.current = null;
            animateText(nextTarget);
          }
        }
      };

      const backspacePhase = () => {
        if (currentText.current.length > 0) {
          setIsBackspacing(true);
          setIsTyping(false);
          isTypingRef.current = false;
          currentText.current = currentText.current.slice(0, -1);
          setDisplayText(currentText.current);
          timeoutRef.current = setTimeout(backspacePhase, backspaceSpeed);
        } else {
          setIsBackspacing(false);
          typePhase();
        }
      };

      timeoutRef.current = setTimeout(() => {
        if (currentTextValue.length > 0) {
          backspacePhase();
        } else {
          typePhase();
        }
      }, startDelay);
    },
    [typeSpeed, backspaceSpeed, startDelay],
  );

  useEffect(() => {
    if (targetText !== currentTargetRef.current) {
      if (isTypingRef.current) {
        queuedTargetRef.current = targetText;
      } else {
        clearExistingTimeout();
        animateText(targetText);
      }
    }
  }, [targetText, animateText]);

  useEffect(() => {
    return () => {
      clearExistingTimeout();
    };
  }, []);

  return {displayText, isBackspacing, isTyping};
};

export type RunningIndicatorProps = {
  text: string;
  testId?: string;
};

export const RunningIndicator = ({
  text,
  testId = 'running-tool-indicator',
}: RunningIndicatorProps) => {
  const {displayText} = useTypewriterWithBackspace(text);

  return (
    <view.div data-testid={testId}>
      <view.span
        css={{
          animation: `${shimmer} 4s linear infinite`,
          fontSize: 'label.medium',
          background: `linear-gradient(90deg, ${tokens.color.neutral[200].toString()}, ${tokens.color.neutral[400].toString()}, ${tokens.color.text.subdued.toString()}, ${tokens.color.text.$default.toString()}, ${tokens.color.neutral[200].toString()})`,
          backgroundSize: '200% auto',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          color: 'transparent',
        }}
      >
        {displayText}
      </view.span>
    </view.div>
  );
};
